import { Injectable } from '@angular/core';
import { environment } from '../../environments/environment';
import { resolveOAuthRedirectUri, hubOriginFromRedirect } from '../util/oauth-hub.util';

/**
 * Scope de la conexión de cuenta: identidad (para login con Google) + Drive acotado.
 * `drive.file` solo da acceso a los archivos que la propia app crea, nunca al Drive completo.
 */
const CONNECT_SCOPE = 'openid profile email https://www.googleapis.com/auth/drive.file';

@Injectable({ providedIn: 'root' })
export class GoogleOAuthService {
    private clientId = (environment as any).googleClientId || '';
    // Permite apuntar el flujo de conexión a otra app de Google Cloud si algún día
    // se separan; por defecto es la misma que el login.
    private driveClientId = (environment as any).googleDriveClientId || this.clientId;

    /**
     * Solo identidad. Para iniciar sesión con Google, donde no corresponde
     * pedir acceso a Drive.
     */
    openAuthPopup(): Promise<{ provider_id: string; email: string; name: string }> {
        return this.openPopup('openid profile email', 'login');
    }

    /**
     * Conexión única de la cuenta de Google del usuario logueado: vincula la identidad
     * y habilita Drive en un solo consentimiento. Es el mismo flujo desde el perfil
     * y desde el panel de Archivos de un requerimiento.
     */
    openGoogleConnectPopup(): Promise<{ email: string; linked: boolean }> {
        return this.openPopup(CONNECT_SCOPE, 'connect');
    }

    private openPopup(scope: string, flow: 'login' | 'connect'): Promise<any> {
        return new Promise((resolve, reject) => {
            // LOGIN: redirect fijo al hub central por TLD (autorizado una vez en Google),
            //   NO el origin del tenant (que exigiría autorizar cada subdominio nuevo).
            // CONNECT (Drive): se mantiene en el origin del tenant, porque el callback
            //   necesita el token de sesión de localStorage, que NO se comparte entre
            //   subdominios. Mover connect al hub rompería la vinculación de Drive.
            const useHub = flow === 'login';
            const redirectUriRaw = useHub
                ? resolveOAuthRedirectUri('google-callback')
                : window.location.origin + '/google-callback';
            const redirectUri = encodeURIComponent(redirectUriRaw);
            const hubOrigin = hubOriginFromRedirect(redirectUriRaw);
            // El callback (en el hub, para login) necesita saber a qué origin (este tenant)
            // devolver el resultado por postMessage. Viaja en el state.
            const tenantOrigin = window.location.origin;
            const state = encodeURIComponent(`${flow}|${tenantOrigin}`);
            const clientId = flow === 'connect' ? this.driveClientId : this.clientId;
            // access_type=offline + prompt=consent es lo único que hace que Google
            // devuelva refresh_token; sin él no se puede subir a Drive más tarde.
            const url = `https://accounts.google.com/o/oauth2/v2/auth?client_id=${clientId}&response_type=code&redirect_uri=${redirectUri}&scope=${encodeURIComponent(scope)}&state=${state}&access_type=offline&prompt=consent`;

            const popup = window.open(url, 'google-oauth', 'width=500,height=600,scrollbars=yes');
            if (!popup) {
                reject(new Error('Popup blocked'));
                return;
            }

            const handler = (event: MessageEvent) => {
                // El mensaje llega desde el hub central (no desde el propio origin).
                if (event.origin !== hubOrigin && event.origin !== window.location.origin) return;
                if (event.data?.type === 'GOOGLE_OAUTH_SUCCESS') {
                    window.removeEventListener('message', handler);
                    resolve(event.data);
                } else if (event.data?.type === 'GOOGLE_OAUTH_ERROR') {
                    window.removeEventListener('message', handler);
                    reject(new Error(event.data.error));
                }
            };

            window.addEventListener('message', handler);
            setTimeout(() => {
                window.removeEventListener('message', handler);
                reject(new Error('Timeout'));
            }, 120000);
        });
    }
}
